import type {MouseEvent, ReactElement, ReactNode} from 'react';
import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import type {Poster, PosterOpts} from '@PosterWhiteboard/poster/poster.class';
import {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';
import {PageOptions} from '@Components/poster-editor/components/page-options';
import {AddNewPageButton} from '@Components/poster-editor/components/add-new-page-button';
import {PosterPopupMenu} from '@Components/poster-editor/components/poster-popup-menu';
import {TextItemPopup} from '@Components/poster-editor/components/text-item-popup';
import './poster-whiteboard.scss';
import {createPoster, loadPoster} from '@PosterWhiteboard/poster/poster-init';
import '@Components/poster-editor/library/poster-ui-delegate';
import {cancelCanvasCursorMode} from '@Components/poster-editor/library/poster-editor-library';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import type {PosterTypeBackendObject} from '@PosterWhiteboard/poster/poster-backend.types';
import {ClickableDiv} from '@Components/clickable-div';
import {updateFitToScreenOnWhiteboardResize, updateSpellCheckSettingsState} from '@Components/poster-editor/poster-editor-reducer';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {PosterWhiteboardLoader} from '@Components/poster-whiteboard/components/poster-whiteboard-loader';
import {watchIsAudioPlayable} from '@Utils/audio.util';
import {initFontsAndCategoriesAndScript} from '@Libraries/font-library';
import {checkClipboardReadPermissions} from '@Libraries/clipboard-library';
import {ImageItemPopup} from '@Components/poster-editor/components/image-item-popup';
import {config} from '@postermywall/fabricjs-2';
import {addElementToNotifyTest, TestNotificationId} from '@Libraries/test.library';
import styles from './poster-whiteboard.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import PosterChangingOverlay from '@Components/poster-whiteboard/components/poster-changing-overlay';
import {PosterVersionHistoryOverlay} from '@Components/poster-whiteboard/components/poster-version-history-overlay/poster-version-history-overlay';
import {getFitToScreenScale, modeHasBufferForFitToScreen} from '@PosterWhiteboard/poster/poster-scaling.class';
import {CustomItemControls} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls';
import {MarqueeSelectionLayer} from '@Components/poster-editor/components/marquee-selection-layer/marquee-selection-layer';
import {
  EDITOR_BOTTOM_BAR_BOTTOM_OFFSET,
  EDITOR_LEFT_MARGIN,
  EDITOR_RIGHT_MARGIN,
  EDITOR_RIGHT_MARGIN_SMALL,
  ScrollVelocityTracker,
} from '@Components/poster-editor/library/poster-editor-scrolling-library';
import {POSTER_ELEMENT_CONTAINER} from '@Components/poster-whiteboard/poster-whiteboard.types';
import {MOBILE_WHITEBOARD_PADDING} from '@PosterWhiteboard/poster/poster.types';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {PosterWhiteboardDragDropOverlay} from '@Components/poster-editor/components/poster-whiteboard-drag-drop-overlay/poster-whiteboard-drag-drop-overlay';
import {PosterGestures} from '@PosterWhiteboard/poster/poster-gestures.class';
import {useCanvasDropTarget} from '@Components/poster-whiteboard/use-canvas-drop-target';
import {TimelineOpenSource} from '@Libraries/ga-events';
import {trackTimelineOpen} from '@Libraries/poster-editor-tracking.library';

// TODO: Move this to a better place
window.isPosterMaker = true;
window.posterEditor = {
  whiteboard: undefined,
  config: undefined,
  elements: {},
};

interface PosterWhiteboardProps {
  className?: string;
  isCenterAligned?: boolean;
  isMobileDisplay?: boolean;
  whiteboardClassName?: string;
  postWhiteboardElement?: ReactNode;
  posterOpts: PosterOpts;
  isWebpage?: boolean;

  onInit?(): void;

  onError?(): void;
}

export type InitPosterOpts = LoadPosterOpts | CreatePosterOpts;

export interface LoadPosterOpts {
  posterHashId: string;
}

export interface CreatePosterOpts {
  posterType: PosterTypeBackendObject;
}

export function PosterWhiteboard({
  className = '',
  isCenterAligned = true,
  isMobileDisplay = false,
  whiteboardClassName = '',
  isWebpage = false,
  onInit = (): void => {},
  onError = (): void => {},
  ...props
}: PosterWhiteboardProps): ReactElement<any> {
  const posterEl = useRef<HTMLDivElement>(null);
  const postPosterElement = useRef<HTMLDivElement>(null);
  const whiteboardContainerRef = useRef<HTMLDivElement>(null);
  const whiteboardRef = useRef<HTMLDivElement>(null);
  const [isPosterLoading, setIsPosterLoading] = useState(false);
  const [scale, setScale] = useState<number | undefined>(undefined);
  const isPickingModeEnabled = useAppSelector((state) => {
    return state.posterEditor.isPickingModeEnabled;
  });
  const isSidebarSmall = useAppSelector((state) => {
    return state.posterEditor.isSidebarSmall;
  });
  const isRemoveBgModeEnabled = useAppSelector((state) => {
    return state.posterEditor.isRemoveBGModeEnabled;
  });
  const areStylesCopied = useAppSelector((state) => {
    return state.posterEditor.copiedStyles;
  });
  const isInGeneration = useAppSelector((state) => {
    return state.posterEditor.posterObject?.mode.details.type === PosterModeType.GENERATE;
  });
  const isDrawModeEnabled = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing.isDrawModeOn;
  });
  const dispatch = useAppDispatch();
  const isPosterOverlayLoaderVisible = useAppSelector((state) => {
    return state.posterEditor.isPosterOverlayLoaderActive;
  });
  const isDesktopTimelineToggleOn = useAppSelector((state) => {
    return state.posterEditor.isDesktopTimelineToggleOn;
  });
  const bottomBarHeight = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.bottomBarHeight;
  });
  const posterRef = useRef<Poster | undefined>(undefined);
  const scrollVelocityTracker = useRef(new ScrollVelocityTracker());
  const isVersionHistoryModeActive = useAppSelector((state) => {
    return state.posterEditor.isVersionHistoryModeActive;
  });

  useCanvasDropTarget();

  useEffect(() => {
    watchIsAudioPlayable();
    bindMediaButton();

    let mountedRef = true;
    if (whiteboardContainerRef.current && whiteboardRef.current && posterEl.current && postPosterElement.current && window.posterEditor) {
      window.posterEditor.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER] = $(whiteboardContainerRef.current);
      window.posterEditor.elements[WhiteboardDomElement.POST_POSTER_ELEMENT] = $(postPosterElement.current);
      window.posterEditor.elements[WhiteboardDomElement.WHITEBOARD] = $(whiteboardRef.current);
    }

    whiteboardContainerRef.current?.addEventListener('wheel', handleWheelEvent, {passive: false});
    whiteboardContainerRef.current?.addEventListener('scroll', handleScrollEvent, {passive: true});

    initPoster()
      .then((p) => {
        posterRef.current = p;
        if (!mountedRef) {
          p.dispose();
        }
        onInit();
      })
      .catch((e) => {
        // TODO: Show error in UI
        console.error(e);
        onError();
      })
      .finally(() => {
        addElementToNotifyTest(TestNotificationId.POSTER_INITIALIZED);
      });

    return (): void => {
      unbindMediaButton();
      mountedRef = false;
      whiteboardContainerRef.current?.removeEventListener('wheel', handleWheelEvent);
      whiteboardContainerRef.current?.removeEventListener('scroll', handleScrollEvent);
      cancelCanvasCursorMode();
      posterRef.current?.dispose();
    };
  }, []);

  useLayoutEffect(() => {
    const scaling = window.posterEditor?.whiteboard?.scaling;
    if (!scaling) {
      return;
    }
    scaling.fitToScreen(true, 300);
    window.PMW.redux.store.dispatch(updateFitToScreenOnWhiteboardResize(false));
  }, [isDesktopTimelineToggleOn]);

  const handleScrollEvent = (): void => {
    window.posterEditor?.whiteboard?.debouncedUpdateCurrentPageBasedOnVisibility();

    if (PosterGestures.shouldEnableGestures() && isEditorMobileVariant()) {
      return; // handled by poster-gestures.class.ts
    }

    const container = whiteboardContainerRef.current;
    if (container) {
      scrollVelocityTracker.current.recordSample(container.scrollTop, container.scrollLeft);
    }

    const scale = window.posterEditor?.whiteboard?.scaling.scale ?? 1;
    const preemptiveRenderAheadBuffer = scrollVelocityTracker.current.computeRenderAheadBuffer(scale);
    window.posterEditor?.whiteboard?.refreshAllVisibleCanvases(preemptiveRenderAheadBuffer);
  };

  const handleWheelEvent = (e: WheelEvent): void => {
    if (window.visualViewport?.scale !== 1) {
      return;
    }

    e.stopPropagation();

    if (e.ctrlKey) {
      window.posterEditor?.whiteboard?.scaling.zoomOnWheelEvent(e);
    }
  };

  const initPoster = async (): Promise<Poster> => {
    await initFontsAndCategoriesAndScript();
    await checkClipboardReadPermissions();
    // Fabric's canvas-drawn marquee is replaced by MarqueeSelectionLayer on desktop; touch never used it (single-finger drag pans).
    config.disableGroupSelector = true;
    let poster;
    if (props.posterOpts.mode.details.type === PosterModeType.CREATE) {
      poster = await createNewPoster();
    } else {
      poster = await loadExistingPoster();
    }
    poster.updateTitle();

    if (isWebpage) {
      trackTimelineOpen(TimelineOpenSource.WEBPAGE_AUTO);
      poster.updateIsTimelineMode(true);
      poster.setPosterVolume(0);
    }

    // The mobile editor loads paused: playback waits for an explicit play tap instead of
    // autoplaying the design the moment it loads.
    if (!poster.mode.isGeneration() && !isMobileDisplay) {
      void poster.play();
    }
    return poster;
  };

  const createNewPoster = async (): Promise<Poster> => {
    if (!posterEl.current) {
      throw new Error('No html element to init poster with');
    }

    setIsPosterLoading(true);
    setScale(() => {
      return getFitToScreenScale(
        props.posterOpts.mode.details.posterWidth,
        props.posterOpts.mode.details.posterHeight,
        modeHasBufferForFitToScreen(props.posterOpts.mode.details.type)
      );
    });
    const poster = await createPoster(posterEl.current, props.posterOpts);
    setIsPosterLoading(false);
    trackPosterBuilderGA4Events(GA4EventName.CREATE_POSTER);
    return poster;
  };

  const loadExistingPoster = async (): Promise<Poster> => {
    if (!posterEl.current) {
      throw new Error('No html element to init poster with');
    }

    setIsPosterLoading(true);
    setScale(() => {
      return getFitToScreenScale(
        props.posterOpts.mode.details.posterWidth,
        props.posterOpts.mode.details.posterHeight,
        modeHasBufferForFitToScreen(props.posterOpts.mode.details.type)
      );
    });

    const poster = await loadPoster(
      posterEl.current,
      {
        onBackendObjectLaoded: () => {
          setIsPosterLoading(false);
        },
      },
      props.posterOpts
    );
    return poster;
  };

  const onWhiteboardContainerClick = (e: MouseEvent): void => {
    if (e.target === whiteboardRef.current || e.target === whiteboardContainerRef.current) {
      dispatch(updateSpellCheckSettingsState(false));
      window.posterEditor?.whiteboard?.clearSelection();
      exitDrawModeIfEnabled();

      if (isWebpage && window.posterEditor?.whiteboard?.pages.poster) {
        const poster = window.posterEditor.whiteboard;
        if (poster.isInitialized) {
          poster.pages.poster.togglePlayPause();
        }
      }
    }
  };

  const bindMediaButton = (): void => {
    if (window.navigator?.mediaSession) {
      navigator.mediaSession.setActionHandler('play', (): void => {
        void window.posterEditor?.whiteboard?.play();
      });
      navigator.mediaSession.setActionHandler('pause', (): void => {
        void window.posterEditor?.whiteboard?.pause();
      });
    }
  };

  const unbindMediaButton = (): void => {
    if (window.navigator?.mediaSession) {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
    }
  };

  const exitDrawModeIfEnabled = (): void => {
    if (isDrawModeEnabled) {
      window.posterEditor?.whiteboard?.drawing.updateFromObject({
        isDrawModeOn: false,
      });
    }
  };

  const getVerticalPaddingForWhiteboard = (): string => {
    if (isMobileDisplay || isInGeneration || isWebpage) return '';

    return isDesktopTimelineToggleOn ? styles.verticalPaddingForZoomedInPoster : styles.generalVerticalPadding;
  };

  const getWhiteboardContainerPadding = (): {
    paddingLeft: string;
    paddingRight: string;
    paddingBottom?: string;
  } => {
    if (isMobileDisplay) {
      return {paddingLeft: `${MOBILE_WHITEBOARD_PADDING}px`, paddingRight: `${MOBILE_WHITEBOARD_PADDING}px`, paddingBottom: `${MOBILE_WHITEBOARD_PADDING}px`};
    }

    if (isInGeneration || props.posterOpts.mode.details.type === PosterModeType.WEB_PAGE) {
      return {paddingLeft: '0', paddingRight: '0', paddingBottom: '0'};
    }

    const leftPadding = `${EDITOR_LEFT_MARGIN}px`;
    const rightPadding = `${isSidebarSmall ? EDITOR_RIGHT_MARGIN_SMALL : EDITOR_RIGHT_MARGIN}px`;

    return {
      paddingLeft: leftPadding,
      paddingRight: rightPadding,
      paddingBottom: isDesktopTimelineToggleOn ? `${(bottomBarHeight ?? 0) + EDITOR_BOTTOM_BAR_BOTTOM_OFFSET}px` : undefined,
    };
  };

  return (
    <ClickableDiv
      id="poster-whiteboard-container"
      style={{
        ...getWhiteboardContainerPadding(),
      }}
      className={`flex-h-row whiteboard-container ${!isWebpage ? 'spacing-p-t-5' : ''} ${isMobileDisplay ? '' : styles.containerForDesktop} ${getVerticalPaddingForWhiteboard()} ${styles.container} ${className} ${isMobileDisplay ? styles.hideScroll : styles.showScroll}`}
      enableFocusOutline={false}
      ref={whiteboardContainerRef}
      onMouseDown={onWhiteboardContainerClick}
      onClick={() => {}}
    >
      <PosterWhiteboardDragDropOverlay whiteboardContainerRef={whiteboardContainerRef} />
      {isPosterLoading && !isPosterOverlayLoaderVisible ? <PosterWhiteboardLoader isCenterAligned={isCenterAligned} posterMode={props.posterOpts.mode} /> : null}
      {isPosterOverlayLoaderVisible ? (
        <PosterChangingOverlay
          posterOpts={props.posterOpts}
          isPosterLoading={isPosterLoading}
          className={`${styles.posterOverlayLoader} ${styles.allMargin}`}
          width={scale ? scale * props.posterOpts.mode.details.posterWidth : undefined}
          height={scale ? scale * props.posterOpts.mode.details.posterHeight : undefined}
        />
      ) : null}
      <div
        ref={whiteboardRef}
        className={`${styles.whiteboard} ${isPosterLoading || isPosterOverlayLoaderVisible ? '_hidden' : ''} ${
          isCenterAligned ? `flex-items-center ${styles.centerAlignedWhiteboard}` : ''
        } flex-v-row ${whiteboardClassName}`}
      >
        {/*Move the isPickingModeEnabled || areStylesCopied check to a function called doShowCursorOverlay, ands rename containerWhilePickingModeEnabled to containerWithCursorOverlay*/}
        <div
          ref={posterEl}
          className={`${POSTER_ELEMENT_CONTAINER} flex-v-row ${styles.posterElement} ${isPickingModeEnabled || areStylesCopied || isRemoveBgModeEnabled ? styles.containerWhilePickingModeEnabled : ''} ${isVersionHistoryModeActive ? styles.canvasDisabledInVersionHistory : ''} ${isPosterOverlayLoaderVisible ? '_hidden' : ''}`}
        >
          <CustomItemControls />
          <MarqueeSelectionLayer />
          <TextItemPopup />
          <ImageItemPopup />
        </div>
        <div ref={postPosterElement} className={styles.postPosterElement}>
          <AddNewPageButton />
          {props.postWhiteboardElement ?? null}
        </div>
      </div>
      <PosterVersionHistoryOverlay />
      <PageOptions />
      <PosterPopupMenu />
      <div id="buffer" className={styles.buffer}>
        <canvas id="bufferCanvas" width="1" height="1" />
      </div>
    </ClickableDiv>
  );
}
